import React, { useCallback, useRef } from 'react';
import { Box, styled } from '@mui/material';
import { VoiBounds } from '~/state/atoms/toolsAtoms';
import { PlaneView, Orientations } from '~/engine/view/views/PlaneView';
import { useWindowMouseDrag } from '~/hooks/useWindowMouseDrag';

const ProjectedVoiBoxHandle = styled(Box)`
    position: absolute;
    width: 10px;
    height: 10px;
    background-color: ${({ theme }) => theme.palette.secondary.main};
    border: 1px solid ${({ theme }) => theme.palette.secondary.light};
    z-index: 6;
    pointer-events: auto;
`;

enum DragAxis {
    X = 'X',
    Y = 'Y',
    Z = 'Z',
}

enum HandleType {
    MOVE = 'move',
    TOP_LEFT = 'top-left',
    TOP_RIGHT = 'top-right',
    BOTTOM_LEFT = 'bottom-left',
    BOTTOM_RIGHT = 'bottom-right',
    TOP = 'top',
    BOTTOM = 'bottom',
    LEFT = 'left',
    RIGHT = 'right',
}

interface DragState {
    type: HandleType;
    startMouse: { x: number; y: number };
    startVoi: VoiBounds;
}

interface ProjectedVoiBoxProps {
    rect: { left: number; top: number; width: number; height: number };
    voi: VoiBounds;
    setVoi: (v: VoiBounds) => void;
    view: PlaneView;
    orientation: Orientations;
}

const ensureValidBounds = (voi: VoiBounds): VoiBounds => ({
    minX: Math.min(voi.minX, voi.maxX),
    maxX: Math.max(voi.minX, voi.maxX),
    minY: Math.min(voi.minY, voi.maxY),
    maxY: Math.max(voi.minY, voi.maxY),
    minZ: Math.min(voi.minZ, voi.maxZ),
    maxZ: Math.max(voi.minZ, voi.maxZ),
});

export default function ProjectedVoiBoxWithHandles({
    rect,
    voi,
    setVoi,
    view,
    orientation,
}: ProjectedVoiBoxProps) {
    const dragRef = useRef<DragState | null>(null);

    const viewportRect = view.canvas?.getBoundingClientRect();
    const visibleLeft = Math.max(rect.left, 0);
    const visibleTop = Math.max(rect.top, 0);
    const visibleWidth = Math.min(
        rect.width,
        (viewportRect?.width ?? 0) - visibleLeft
    );
    const visibleHeight = Math.min(
        rect.height,
        (viewportRect?.height ?? 0) - visibleTop
    );
    const offsetX = rect.left - visibleLeft;
    const offsetY = rect.top - visibleTop;

    const moveAxis = (
        voiBounds: VoiBounds,
        axis: DragAxis,
        sign: 1 | -1,
        delta: { x: number; y: number; z: number }
    ): VoiBounds => {
        const deltaValue = delta[axis.toLowerCase() as 'x' | 'y' | 'z'];
        const key = (
            sign === 1 ? `max${axis}` : `min${axis}`
        ) as keyof VoiBounds;
        return { ...voiBounds, [key]: voiBounds[key] + deltaValue };
    };

    const onDrag = useCallback(
        (e: MouseEvent) => {
            if (!dragRef.current) {
                return;
            }
            const { type, startMouse, startVoi } = dragRef.current;

            const dx = e.clientX - startMouse.x;
            const dy = e.clientY - startMouse.y;

            const startPatientCoords = view.patientCoordsFromCanvasCoords(
                startMouse.x,
                startMouse.y
            );
            const currentPatientCoords = view.patientCoordsFromCanvasCoords(
                startMouse.x + dx,
                startMouse.y + dy
            );

            const delta = {
                x: currentPatientCoords[0] - startPatientCoords[0],
                y: currentPatientCoords[1] - startPatientCoords[1],
                z: currentPatientCoords[2] - startPatientCoords[2],
            };

            let newVoi = { ...startVoi };

            if (type === HandleType.MOVE) {
                Object.values(DragAxis).forEach((axis) => {
                    newVoi = moveAxis(newVoi, axis, 1, delta);
                    newVoi = moveAxis(newVoi, axis, -1, delta);
                });
            } else {
                switch (orientation) {
                    case Orientations.TRANSVERSAL_ORIENTATION:
                        // Horizontal axis: X, Vertical axis: Y
                        if (
                            type === HandleType.LEFT ||
                            type === HandleType.TOP_LEFT ||
                            type === HandleType.BOTTOM_LEFT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.X, -1, delta);
                        }
                        if (
                            type === HandleType.RIGHT ||
                            type === HandleType.TOP_RIGHT ||
                            type === HandleType.BOTTOM_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.X, 1, delta);
                        }
                        if (
                            type === HandleType.TOP ||
                            type === HandleType.TOP_LEFT ||
                            type === HandleType.TOP_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Y, -1, delta);
                        }
                        if (
                            type === HandleType.BOTTOM ||
                            type === HandleType.BOTTOM_LEFT ||
                            type === HandleType.BOTTOM_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Y, 1, delta);
                        }
                        break;

                    case Orientations.CORONAL_ORIENTATION:
                        // Horizontal axis: X, Vertical axis: Z (inverted)
                        if (
                            type === HandleType.LEFT ||
                            type === HandleType.TOP_LEFT ||
                            type === HandleType.BOTTOM_LEFT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.X, -1, delta);
                        }
                        if (
                            type === HandleType.RIGHT ||
                            type === HandleType.TOP_RIGHT ||
                            type === HandleType.BOTTOM_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.X, 1, delta);
                        }
                        if (
                            type === HandleType.TOP ||
                            type === HandleType.TOP_LEFT ||
                            type === HandleType.TOP_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Z, 1, delta);
                        }
                        if (
                            type === HandleType.BOTTOM ||
                            type === HandleType.BOTTOM_LEFT ||
                            type === HandleType.BOTTOM_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Z, -1, delta);
                        }
                        break;

                    case Orientations.SAGITTAL_ORIENTATION:
                        // Horizontal axis: Y, Vertical axis: Z (inverted)
                        if (
                            type === HandleType.LEFT ||
                            type === HandleType.TOP_LEFT ||
                            type === HandleType.BOTTOM_LEFT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Y, 1, delta);
                        }
                        if (
                            type === HandleType.RIGHT ||
                            type === HandleType.TOP_RIGHT ||
                            type === HandleType.BOTTOM_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Y, -1, delta);
                        }
                        if (
                            type === HandleType.TOP ||
                            type === HandleType.TOP_LEFT ||
                            type === HandleType.TOP_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Z, 1, delta);
                        }
                        if (
                            type === HandleType.BOTTOM ||
                            type === HandleType.BOTTOM_LEFT ||
                            type === HandleType.BOTTOM_RIGHT
                        ) {
                            newVoi = moveAxis(newVoi, DragAxis.Z, -1, delta);
                        }
                        break;
                }
            }

            setVoi(ensureValidBounds(newVoi));
        },
        [orientation, setVoi, view]
    );

    const onEnd = useCallback(() => {
        dragRef.current = null;
    }, []);

    const startDrag = useWindowMouseDrag(onDrag, onEnd);

    const beginDrag = (e: React.MouseEvent, type: HandleType) => {
        dragRef.current = {
            type,
            startMouse: { x: e.clientX, y: e.clientY },
            startVoi: { ...voi },
        };
        startDrag(e);
    };

    const edgeThickness = 6;

    const handles = [
        // Full interior for move (rendered first, behind edges/corners)
        {
            type: HandleType.MOVE,
            style: {
                left: edgeThickness,
                right: edgeThickness,
                top: edgeThickness,
                bottom: edgeThickness,
                width: 'auto',
                height: 'auto',
                cursor: 'move',
                background: 'transparent',
                border: 'none',
            },
        },
        // Edge handles (invisible bars along each border)
        {
            type: HandleType.TOP,
            style: {
                left: edgeThickness,
                right: edgeThickness,
                top: -edgeThickness / 2,
                height: edgeThickness,
                width: 'auto',
                cursor: 'ns-resize',
                background: 'transparent',
                border: 'none',
            },
        },
        {
            type: HandleType.BOTTOM,
            style: {
                left: edgeThickness,
                right: edgeThickness,
                bottom: -edgeThickness / 2,
                height: edgeThickness,
                width: 'auto',
                cursor: 'ns-resize',
                background: 'transparent',
                border: 'none',
            },
        },
        {
            type: HandleType.LEFT,
            style: {
                top: edgeThickness,
                bottom: edgeThickness,
                left: -edgeThickness / 2,
                width: edgeThickness,
                height: 'auto',
                cursor: 'ew-resize',
                background: 'transparent',
                border: 'none',
            },
        },
        {
            type: HandleType.RIGHT,
            style: {
                top: edgeThickness,
                bottom: edgeThickness,
                right: -edgeThickness / 2,
                width: edgeThickness,
                height: 'auto',
                cursor: 'ew-resize',
                background: 'transparent',
                border: 'none',
            },
        },
        // Corner handles
        {
            type: HandleType.TOP_LEFT,
            style: {
                left: 0,
                top: 0,
                transform: 'translate(-50%, -50%)',
                cursor: 'nwse-resize',
            },
        },
        {
            type: HandleType.TOP_RIGHT,
            style: {
                right: 0,
                top: 0,
                transform: 'translate(50%, -50%)',
                cursor: 'nesw-resize',
            },
        },
        {
            type: HandleType.BOTTOM_LEFT,
            style: {
                left: 0,
                bottom: 0,
                transform: 'translate(-50%, 50%)',
                cursor: 'nesw-resize',
            },
        },
        {
            type: HandleType.BOTTOM_RIGHT,
            style: {
                right: 0,
                bottom: 0,
                transform: 'translate(50%, 50%)',
                cursor: 'nwse-resize',
            },
        },
    ];

    return (
        <Box
            data-testid="voi-box"
            sx={{
                position: 'absolute',
                left: visibleLeft,
                top: visibleTop,
                width: visibleWidth,
                height: visibleHeight,
                overflow: 'hidden',
                pointerEvents: 'none',
            }}
        >
            <Box
                sx={(theme) => ({
                    position: 'absolute',
                    left: offsetX,
                    top: offsetY,
                    width: rect.width,
                    height: rect.height,
                    border: `2px solid ${theme.palette.secondary.main}`,
                    background: 'rgba(244, 152, 0, 0.15)',
                    zIndex: 5,
                    pointerEvents: 'none',
                })}
            >
                {handles.map(({ type, style }) => (
                    <ProjectedVoiBoxHandle
                        key={type}
                        data-testid={`voi-handle-${type}`}
                        onMouseDown={(e) => beginDrag(e, type)}
                        style={style}
                    />
                ))}
            </Box>
        </Box>
    );
}
